import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Box, Flex, Text } from "@chakra-ui/react";
import { useNavigate, useParams } from "react-router-dom";
import moment from "moment";
import { AppBar } from "../../../components/mobileUi";
import {
  CenterSpinner,
  EmptyText,
  FilterField,
  FilterPanel,
  LoadMore,
  M,
  Png,
  RowKV,
  SelectSheet,
  StatCard,
  SummaryCard,
  buildMonthOptions,
  useSheet,
} from "../../../components/managementUi";
import {
  DesktopPage,
  DesktopPanel,
  DesktopStatRow,
  DesktopTable,
  useIsDesktop,
} from "../../../components/desktopUi";
import { appImages } from "../../../assets/appicons";
import { formatMoneyToString, formatMoneyUS } from "../../../utils/financeFormat";
import { useEmployeeAuth } from "../../../auth/EmployeeAuthContext";
import { JOB_TITLE, filterJobPosition } from "../../../utils/managerRole";
import { ALL_OPTION, buildOptionSheetData, useFilterOptions, validId } from "../../../utils/useFilterOptions";
import {
  getIncomeCompareAreaListApi,
  getIncomeCompareAreaStatisticApi,
  getIncomeCompareBlockListApi,
  getIncomeCompareBlockStatisticApi,
  getIncomeCompareEmployeeListApi,
  getIncomeCompareEmployeeStatisticApi,
} from "../../../api/incomeManager.api";
import { EMP_BASE_PATH } from "../../../config";

const LIMIT = 10;

// Cấu hình 3 biến thể (nhân viên / vùng / khối) - khớp 3 màn của app.
export const INCOME_KINDS = {
  "nhan-vien": {
    title: "Thu nhập nhân viên",
    summaryText: "TỔNG SỐ NHÂN VIÊN",
    listApi: getIncomeCompareEmployeeListApi,
    statisticApi: getIncomeCompareEmployeeStatisticApi,
    transactionsTitle: "Giao dịch đã được đối chiếu",
    cardTitle: (item) =>
      `${item?.employeeObject?.fullName || ""} ${item?.employeeObject?.fullName ? "-" : ""} ${
        item?.employeeObject?.code || ""
      }`.trim(),
    // Khối/Vùng/Chi nhánh nằm ở gốc item (không phải trong employeeObject) - khớp app.
    extraRows: [
      { label: "Khối", path: (i) => i?.saleBlock?.name },
      { label: "Vùng", path: (i) => i?.saleArea?.name },
      { label: "Chi nhánh", path: (i) => i?.branch?.name },
    ],
    idOf: (i) => i?.employeeObject?._id,
    nameOf: (i) => i?.employeeObject?.fullName,
  },
  vung: {
    title: "Thu nhập của vùng",
    summaryText: "TỔNG SỐ VÙNG",
    listApi: getIncomeCompareAreaListApi,
    statisticApi: getIncomeCompareAreaStatisticApi,
    transactionsTitle: "Giao dịch đã đối chiếu",
    cardTitle: (item) =>
      `${item?.saleArea?.name || ""} ${item?.saleArea?.name ? "-" : ""} ${item?.saleArea?.code || ""}`.trim(),
    extraRows: [],
    idOf: (i) => i?.saleArea?._id,
    nameOf: (i) => i?.saleArea?.name,
  },
  khoi: {
    title: "Thu nhập khối",
    summaryText: "TỔNG SỐ KHỐI",
    listApi: getIncomeCompareBlockListApi,
    statisticApi: getIncomeCompareBlockStatisticApi,
    transactionsTitle: "Giao dịch đã đối chiếu",
    cardTitle: (item) =>
      `${item?.saleBlock?.name || ""} ${item?.saleBlock?.name ? "-" : ""} ${item?.saleBlock?.code || ""}`.trim(),
    extraRows: [],
    idOf: (i) => i?.saleBlock?._id,
    nameOf: (i) => i?.saleBlock?.name,
  },
};

const SORT_BY_OPTIONS = [
  { key: "INCOME", label: "Sắp xếp theo thu nhập", sortBy: "INCOME" },
  { key: "TRANSACTIONS", label: "Sắp xếp theo số giao dịch", sortBy: "TRANSACTIONS" },
  { key: "TOTAL_VALUE", label: "Sắp xếp theo giá trị niêm yết", sortBy: "TOTAL_VALUE" },
];
const SORT_ORDER_OPTIONS = [
  { key: "NEWEST", label: "Giảm dần", sortOrder: "desc" },
  { key: "OLDEST", label: "Tăng dần", sortOrder: "asc" },
];

const CompareCard = ({ item, cfg, onClick }) => (
  <Box
    as="button"
    type="button"
    w="100%"
    textAlign="left"
    onClick={onClick}
    bg="white"
    border="1px solid"
    borderColor={M.border}
    borderRadius="12px"
    p="14px"
  >
    <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={2}>
      {cfg.cardTitle(item) || "-"}
    </Text>
    <Box h="1px" bg={M.border} mt="12px" mb="12px" />
    <Flex direction="column" gap="8px">
      <RowKV label="Thu nhập đối chiếu" value={formatMoneyUS(item?.totalIncome || 0)} valueColor={M.primary} />
      <RowKV label="Số giao dịch đối chiếu" value={String(item?.totalTransactions || 0)} />
      <RowKV label="Tổng giá trị niêm yết" value={formatMoneyUS(item?.totalListedValue || 0)} valueColor={M.primary} />
      {cfg.extraRows.map((r) => (
        <RowKV key={r.label} label={r.label} value={r.path(item) || "-"} />
      ))}
    </Flex>
  </Box>
);

const IncomeCompareList = () => {
  const navigate = useNavigate();
  const isDesktop = useIsDesktop();
  const { kind } = useParams();
  const cfg = INCOME_KINDS[kind];
  const { user } = useEmployeeAuth();
  const jobPosition = filterJobPosition(user);
  const monthOptions = useMemo(() => buildMonthOptions(24), []);

  const [filterOpen, setFilterOpen] = useState(false);
  const [monthValue, setMonthValue] = useState(() => moment().format("MM-YYYY"));
  const [area, setArea] = useState(ALL_OPTION);
  const [block, setBlock] = useState(ALL_OPTION);
  const [branch, setBranch] = useState(ALL_OPTION);
  const [sortByKey, setSortByKey] = useState("INCOME");
  const [sortOrderKey, setSortOrderKey] = useState("NEWEST");

  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stat, setStat] = useState(null);
  const [page, setPage] = useState(1);
  const [hasNext, setHasNext] = useState(false);
  const [loading, setLoading] = useState(false);
  const reqId = useRef(0);

  const sheet = useSheet();
  const [sheetSearch, setSheetSearch] = useState("");
  const sheetExtra = useMemo(
    () => (sheet.type === "BLOCK" ? { saleAreaId: validId(area) } : undefined),
    [sheet.type, area]
  );
  const options = useFilterOptions(sheet.type, sheetSearch, sheetExtra);

  const sortByOption = SORT_BY_OPTIONS.find((s) => s.key === sortByKey) || SORT_BY_OPTIONS[0];
  const sortOrderOption = SORT_ORDER_OPTIONS.find((s) => s.key === sortOrderKey) || SORT_ORDER_OPTIONS[0];

  const buildParams = useCallback(
    (p) => {
      const [month, year] = monthValue.split("-");
      return {
        page: p,
        limit: LIMIT,
        sortBy: sortByOption.sortBy,
        sortOrder: sortOrderOption.sortOrder,
        saleAreaId: validId(area),
        saleBlockId: validId(block),
        branchId: validId(branch),
        month,
        year,
      };
    },
    [monthValue, sortByOption, sortOrderOption, area, block, branch]
  );

  const fetchPage = useCallback(
    async (p, mode) => {
      if (!cfg) return;
      reqId.current += 1;
      const rid = reqId.current;
      setLoading(true);
      try {
        const params = buildParams(p);
        const [data, statistic] = await Promise.all([
          cfg.listApi(params),
          p === 1 ? cfg.statisticApi(params).catch(() => null) : Promise.resolve(null),
        ]);
        if (rid !== reqId.current) return;
        const list = data?.items || [];
        setItems((prev) => (mode === "append" ? [...prev, ...list] : list));
        setHasNext(!!data?.pagination?.hasNextPage);
        setTotal(data?.pagination?.total ?? list.length);
        if (statistic) setStat(statistic);
        setPage(p);
      } catch {
        if (rid === reqId.current && mode !== "append") setItems([]);
      } finally {
        if (rid === reqId.current) setLoading(false);
      }
    },
    [cfg, buildParams]
  );

  useEffect(() => {
    fetchPage(1, "replace");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [kind, monthValue, sortByKey, sortOrderKey, area?._id, block?._id, branch?._id]);

  const monthLabel = monthOptions.find((m) => m.value === monthValue)?.label || monthValue;

  const filterFields = useMemo(() => {
    const f = {
      branch: (
        <FilterField key="branch" label="Chi nhánh" value={branch?.name || "Tất cả"} onClick={() => sheet.open("BRANCH")} />
      ),
      area: <FilterField key="area" label="Vùng" value={area?.name || "Tất cả"} onClick={() => sheet.open("AREA")} />,
      block: <FilterField key="block" label="Khối" value={block?.name || "Tất cả"} onClick={() => sheet.open("BLOCK")} />,
      month: (
        <FilterField key="month" label="Tháng đối chiếu" value={monthLabel} onClick={() => sheet.open("MONTH")} width="100%" />
      ),
    };
    switch (jobPosition) {
      case JOB_TITLE.TRUONG_KHOI:
        return [f.branch, f.month];
      case JOB_TITLE.TRUONG_VUNG:
        return [f.branch, f.block, f.month];
      case JOB_TITLE.TGD:
        return [f.branch, f.area, f.block, f.month];
      default:
        return [f.branch, f.area, f.block, f.month];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobPosition, branch, area, block, monthLabel]);

  const sheetTitle =
    { AREA: "Vùng", BLOCK: "Khối", BRANCH: "Chi nhánh", MONTH: "Chọn tháng", SORT_BY: "Sắp xếp theo", SORT_ORDER: "Thứ tự" }[
      sheet.type
    ] || "";

  const sheetData = useMemo(() => {
    if (sheet.type === "MONTH") return monthOptions.map((x) => ({ key: x.value, label: x.label, raw: x.value }));
    if (sheet.type === "SORT_BY") return SORT_BY_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "SORT_ORDER") return SORT_ORDER_OPTIONS.map((x) => ({ key: x.key, label: x.label, raw: x.key }));
    if (sheet.type === "AREA") return buildOptionSheetData(options.items, area);
    if (sheet.type === "BLOCK") return buildOptionSheetData(options.items, block);
    if (sheet.type === "BRANCH") return buildOptionSheetData(options.items, branch);
    return [];
  }, [sheet.type, options.items, area, block, branch, monthOptions]);

  const selectedKey = {
    MONTH: monthValue,
    SORT_BY: sortByKey,
    SORT_ORDER: sortOrderKey,
    AREA: area?._id,
    BLOCK: block?._id,
    BRANCH: branch?._id,
  }[sheet.type];

  const onPick = (raw) => {
    if (sheet.type === "MONTH") setMonthValue(raw);
    if (sheet.type === "SORT_BY") setSortByKey(raw);
    if (sheet.type === "SORT_ORDER") setSortOrderKey(raw);
    if (sheet.type === "AREA") setArea(raw);
    if (sheet.type === "BLOCK") setBlock(raw);
    if (sheet.type === "BRANCH") setBranch(raw);
    sheet.close();
    setSheetSearch("");
  };

  if (!cfg) {
    return (
      <Box maxW="480px" mx="auto">
        <AppBar title="Quản lý thu nhập" />
        <EmptyText text="Không tìm thấy loại thống kê" />
      </Box>
    );
  }

  const selectSheet = (
    <SelectSheet
      isOpen={sheet.isOpen}
      onClose={() => {
        sheet.close();
        setSheetSearch("");
      }}
      title={sheetTitle}
      data={sheetData}
      selectedKey={selectedKey}
      onSelect={onPick}
      searchable={["AREA", "BLOCK", "BRANCH"].includes(sheet.type)}
      searchValue={sheetSearch}
      onChangeSearch={setSheetSearch}
      loading={options.loading}
      loadingMore={options.loadingMore}
      hasNextPage={options.hasMore}
      onEndReached={options.loadMore}
    />
  );

  // ===================== Bản WEB (desktop) =====================
  if (isDesktop) {
    const rows = items.map((it, i) => {
      const extra = {};
      cfg.extraRows.forEach((r) => {
        extra[r.label] = r.path(it) || "-";
      });
      return {
        id: it._id || i,
        raw: it,
        name: cfg.cardTitle(it) || "-",
        income: formatMoneyUS(it?.totalIncome || 0),
        transactions: String(it?.totalTransactions || 0),
        listed: formatMoneyUS(it?.totalListedValue || 0),
        ...extra,
      };
    });

    const openDetail = (row) => {
      const [month, year] = monthValue.split("-");
      const id = cfg.idOf(row.raw);
      const name = encodeURIComponent(cfg.nameOf(row.raw) || "");
      navigate(
        `${EMP_BASE_PATH}/quan-ly/thu-nhap/${kind}/${id}?month=${month}&year=${year}&name=${name}`,
      );
    };

    return (
      <DesktopPage title={cfg.title}>
        <DesktopStatRow
          items={[
            { key: "total", title: cfg.summaryText, total: total || 0 },
            {
              key: "income",
              title: "Thu nhập của nhân viên",
              total: formatMoneyToString(stat?.totalIncome || 0),
              color: "#2ABA82",
            },
            {
              key: "trans",
              title: cfg.transactionsTitle,
              total: stat?.totalTransactions ?? 0,
              color: "#E5484D",
            },
            {
              key: "listed",
              title: "Giá trị niêm yết",
              total: formatMoneyToString(stat?.totalListedValue || 0),
              color: "#2F80ED",
            },
            {
              key: "pdv",
              title: "Giá tính phí dịch vụ",
              total: formatMoneyToString(stat?.totalPriceForCommission || 0),
              color: "#8B5CF6",
            },
          ]}
        />

        <DesktopPanel p="0">
          <Flex p="16px" gap="16px" flexWrap="wrap" align="flex-end">
            {filterFields.map((el, i) =>
              React.cloneElement(el, { key: el.key || i, width: "200px" }),
            )}
            <Box w="200px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Sắp xếp theo
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                border="1px solid"
                borderColor={M.border}
                borderRadius="8px"
                bg="white"
                fontSize="13px"
                onClick={() => sheet.open("SORT_BY")}
              >
                {sortByOption.label}
              </Box>
            </Box>
            <Box w="160px">
              <Text fontSize="13px" color={M.subTitle} mb="4px">
                Thứ tự
              </Text>
              <Box
                as="button"
                w="100%"
                h="40px"
                px="12px"
                textAlign="left"
                border="1px solid"
                borderColor={M.border}
                borderRadius="8px"
                bg="white"
                fontSize="13px"
                onClick={() => sheet.open("SORT_ORDER")}
              >
                {sortOrderOption.label}
              </Box>
            </Box>
          </Flex>

          <DesktopTable
            loading={loading && !items.length}
            onRowClick={openDetail}
            rows={rows}
            headers={[
              { key: "name", label: "Tên", align: "left" },
              {
                key: "income",
                label: "Thu nhập đối chiếu",
                align: "right",
                render: (row) => (
                  <Text fontSize="14px" fontWeight={600} color={M.primary}>
                    {row.income}
                  </Text>
                ),
              },
              { key: "transactions", label: "Số giao dịch đối chiếu" },
              {
                key: "listed",
                label: "Tổng giá trị niêm yết",
                align: "right",
                render: (row) => (
                  <Text fontSize="14px" fontWeight={600} color={M.primary}>
                    {row.listed}
                  </Text>
                ),
              },
              ...cfg.extraRows.map((r) => ({ key: r.label, label: r.label, align: "left" })),
            ]}
          />

          <Box px="16px" pb="16px">
            <LoadMore
              hasNext={hasNext}
              loading={loading}
              onClick={() => fetchPage(page + 1, "append")}
            />
          </Box>
        </DesktopPanel>

        {selectSheet}
      </DesktopPage>
    );
  }

  return (
    <Box maxW="480px" mx="auto" pb="40px">
      <AppBar title={cfg.title} />

      <Box px="16px" pt="12px">
        <FilterPanel open={filterOpen} onToggle={() => setFilterOpen((p) => !p)}>
          {filterFields}
        </FilterPanel>

        <Box h="12px" />
        <SummaryCard text={cfg.summaryText} total={total} />

        <Box h="12px" />
        <Flex flexWrap="wrap" justify="space-between" gap="12px">
          <StatCard
            subTitle="THU NHẬP NHÂN VIÊN"
            title="Thu nhập của nhân viên"
            value={formatMoneyToString(stat?.totalIncome || 0)}
            icon={appImages.icon_du_an}
            bgColor="#EDFDF5"
            textColor="#2ABA82"
          />
          <StatCard
            subTitle="GIAO DỊCH"
            title={cfg.transactionsTitle}
            value={stat?.totalTransactions ?? 0}
            icon={appImages.icon_giao_dich}
            bgColor="#FFF1F0"
            textColor="#E5484D"
          />
          <StatCard
            subTitle="GIÁ TRỊ NIÊM YẾT"
            title="Giá trị niêm yết"
            value={formatMoneyToString(stat?.totalListedValue || 0)}
            icon={appImages.icon_gia_niem_yet}
            bgColor="#EFF6FF"
            textColor="#2F80ED"
          />
          <StatCard
            subTitle="GIÁ TÍNH PDV"
            title="Giá tính phí dịch vụ"
            value={formatMoneyToString(stat?.totalPriceForCommission || 0)}
            icon={appImages.icon_PDV}
            bgColor="#F5F3FF"
            textColor="#8B5CF6"
          />
        </Flex>

        <Flex align="center" justify="space-between" mt="16px" mb="14px" gap="8px">
          <Flex
            as="button"
            type="button"
            align="center"
            gap="6px"
            onClick={() => sheet.open("SORT_BY")}
            minW="0"
          >
            <Text fontSize="14px" fontWeight={600} color={M.baseContent} noOfLines={1}>
              {sortByOption.label}
            </Text>
            <Png src={appImages.icon_chevron_down} size={18} />
          </Flex>
          <Flex as="button" type="button" align="center" gap="6px" onClick={() => sheet.open("SORT_ORDER")} flexShrink={0}>
            <Text fontSize="14px" fontWeight={600} color={M.baseContent}>
              {sortOrderOption.label}
            </Text>
            <Png src={appImages.icon_chevron_down} size={18} />
          </Flex>
        </Flex>

        <Text fontSize="14px" fontWeight={600} color={M.baseContent} mb="12px">
          Danh sách giao dịch
        </Text>

        {loading && !items.length ? (
          <CenterSpinner />
        ) : items.length === 0 ? (
          <EmptyText />
        ) : (
          <Flex direction="column" gap="12px">
            {items.map((it, i) => (
              <CompareCard
                key={it._id || i}
                item={it}
                cfg={cfg}
                onClick={() => {
                  const [month, year] = monthValue.split("-");
                  const id = cfg.idOf(it);
                  const name = encodeURIComponent(cfg.nameOf(it) || "");
                  navigate(
                    `${EMP_BASE_PATH}/quan-ly/thu-nhap/${kind}/${id}?month=${month}&year=${year}&name=${name}`
                  );
                }}
              />
            ))}
            <LoadMore hasNext={hasNext} loading={loading} onClick={() => fetchPage(page + 1, "append")} />
          </Flex>
        )}
      </Box>

      {selectSheet}
    </Box>
  );
};

export default IncomeCompareList;
